import {
	ReactElement,
	useEffect,
	useMemo
} from "react";
import { DocumentListItem } from "../../../Items/DocumentListItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { GlobalSearchResultItemKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { GlobalSearchItem } from "../../../GlobalSearchItem/GlobalSearchItem";
import { generateTrackingId } from "client/Misc/utils";
import { convertDocumentResultToDocumentViewModel } from "client/Features/Search/RecentSearch/Persistence/convertPersistedData";
import { useDocumentNavigationHandler } from "../../../Navigation/useDocumentNavigationHandler";
import { ValidGlobalSearchItemPersistedModel } from "client/Features/Search/RecentSearch/Persistence/validation";

export type DocumentItemViewProps = {
	item: ValidGlobalSearchItemPersistedModel<GlobalSearchResultItemKind.Document>;
	itemIndex: number;
	isMobile: boolean;
	timezone: TimeZone;
	onDismiss?: () => void;
	onRemove: () => void;
	tabIndex: number;
};

export const DocumentItemView = (props: DocumentItemViewProps): ReactElement | null => {
	const {
		item,
		itemIndex,
		isMobile,
		timezone,
		onDismiss,
		onRemove,
		tabIndex
	} = props;

	const documentConversion = useMemo(
		(): TryResult<DocumentItemViewModel> => convertDocumentResultToDocumentViewModel(item.document),
		[item.document]
	);

	const openHandler: () => void = useDocumentNavigationHandler({
		document: documentConversion.wasSuccessful
			? documentConversion.result
			: "unresolved",
		recentlyOpenedItem: item
	});

	useEffect(
		(): void => {
			if (!documentConversion.wasSuccessful) {
				console.error("Unable to convert document result item to document view model.");
			}
		},
		[documentConversion.wasSuccessful]
	);

	if (!documentConversion.wasSuccessful) {
		return null;
	}

	return (
		<GlobalSearchItem
			trackingId={generateTrackingId("document-item", `|${itemIndex}`)}
			onClick={openHandler}
		>
			<DocumentListItem
				document={documentConversion.result}
				isMobile={isMobile}
				timezone={timezone}
				onDismiss={onDismiss}
				onRemove={onRemove}
				itemIndex={itemIndex}
				tabIndex={tabIndex}
				onExecute={openHandler}
			/>
		</GlobalSearchItem>
	);
};
DocumentItemView.displayName = "DocumentItemView";